<template>
  <div>
    <div class="grid-x chart-container">
      <div class="cell">
        <div v-if="displayMode === 'double'">
          <div
            class="legendDot"
            :style="{ backgroundColor: legendColor }"
          />
          <div
            class="legend-item"
            :style="{ color: legendColor }"
          >
            {{ legendText }}
          </div>
          <rare-chart-rank
            :total="total"
            :rank="rank"
            :display="!rareMode"
          />
        </div>
      </div>
      <div
        class="cell"
        :class="{
          'small-3': displayMode === 'single',
          'small-5': displayMode === 'double',
        }"
      >
        <ul class="label-list">
          <li
            v-for="n in datapoints"
            :key="n"
          >
            <span
              v-if="displayRankBadge({ client: formatClientName(labels[n - 1]), position: n })"
              class="rankBadge"
              :style="{ backgroundColor: legendColor, marginLeft: rankBadgeLeftMargin }"
            >
              {{ rank }}</span>
            <span
              v-if="rareMode"
              tabindex="0"
              class="link"
              :class="{ labelPadding: rareMode === false }"
              @click="showSummaryPage({
                username: labels[n - 1],
                display_name: formatClientName(labels[n - 1], n),
              })"
              @keypress="showSummaryPage({
                username: labels[n - 1],
                display_name: formatClientName(labels[n - 1], n),
              })"
            >
              {{ formatClientName(labels[n - 1]) }}
            </span>
            <span
              v-if="!rareMode"
              :class="{ labelPadding: rareMode === false }"
            >
              {{ formatClientName(labels[n - 1]) }}
            </span>
          </li>
        </ul>
      </div>
      <div
        class="cell"
        :class="{
          'small-8': displayMode === 'single',
          'small-6': displayMode === 'double',
        }"
      >
        <div
          class="industry-overview-chart-container"
          :style="{ height: chartHeight }"
        >
          <canvas :id="canvasID" />
        </div>
      </div>
      <div class="small-1 cell">
        <ul class="data-list">
          <li
            v-for="n in datapoints"
            :key="n"
          >
            <span :style="{ color: textColors[n - 1] }">
              {{ formatDataLabel(chartData[n - 1]) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import RareChartRank from '../controls/RareChartRank.vue';

export default {
  name: 'RareIndustryOverviewChart',
  components: {
    'rare-chart-rank': RareChartRank,
  },
  props: {
    nameInLabels: {
      type: String,
      required: false,
      default: '',
    },
    total: {
      type: Number,
      required: true,
    },
    rank: {
      type: Number,
      required: true,
    },
    page: {
      type: String,
      required: true,
    },
    chartData: {
      type: Array,
      required: true,
    },
    chartSize: {
      type: String,
      required: false,
      default: '',
    },
    labels: {
      type: Array,
      required: true,
    },
    color: {
      type: String,
      required: false,
      default: 'blue',
    },
    canvasID: {
      type: String,
      required: false,
      default: null,
    },
    legendText: {
      type: String,
      required: false,
      default: '',
    },
    displayMode: {
      type: String,
      required: true,
    },
    maxDecimal: {
      type: Number,
      required: true,
    },
    maxPercentage: {
      type: Number,
      required: true,
    },
    forceRankOffset: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      flagBreakdownChartImg: '',
      barColorArray: [],
      textColorArray: [],
      chartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            display: false,
            categoryPercentage: 1,
            barThickness: 30,
            ticks: {
              display: true,
            },
            gridLines: {
              display: false,
              color: '#FFFFFF',
            },
          }],
          xAxes: [{
            display: false,
            categoryPercentage: 1,
            barThickness: 30,
            ticks: {
              display: false,
            },
          }],
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      singleClientMode: 'singleClientMode',
      dataFormat: 'dataFormat',
      rareMode: 'rareMode',
      currentClient: 'currentClient',
    }),
    dataToRender() {
      let dataToReturn;
      if (this.$store.getters.industryCompAndDecimalException) {
        dataToReturn = this.chartData.slice(0, 1);
      } else {
        dataToReturn = this.chartData;
      }
      return dataToReturn;
    },
    datapoints() {
      return parseInt(this.chartData.length, 10);
    },
    chartHeight() {
      const value = (this.chartData.length * 30) + (this.chartData.length * 10);
      return `${value}px`;
    },
    legendColor() {
      return this.$store.getters.fetchBarColor(this.color);
    },
    rankBadgeLeftMargin() {
      return this.rareMode ? '-40px' : '-10px';
    },
    industryIndex() {
      return this.labels.findIndex((str) => str.includes('ndustry'));
    },
    chartColors() {
      const colorArray = [];

      if (this.rareMode) {
        for (let i = 0; i < this.datapoints; i += 1) {
          colorArray.push(this.$store.getters.fetchBarColor(this.color));
        }
      } else {
        for (let i = 0; i < this.datapoints; i += 1) {
          const clientInList = this.labels[i];
          const username = this.$store.getters.currentClient;
          let barColor;
          if (clientInList === username) {
            barColor = this.color;
          } else {
            barColor = `${this.color}Light`;
          }
          colorArray.push(this.$store.getters.fetchBarColor(barColor));
        }
      }

      colorArray[this.industryIndex] = '#b4bfc8';

      return colorArray;
    },
    textColors() {
      const colorArray = [];
      for (let i = 0; i < this.datapoints; i += 1) {
        colorArray.push(this.$store.getters.fetchTextColor(this.color));
      }
      colorArray[this.industryIndex] = '#7d8b96';
      return colorArray;
    },
  },
  watch: {
    data: function () { // eslint-disable-line
      this.buildChart();
    },
  },
  updated() {
    this.buildChart();
  },
  created() {
    Chart.scaleService.updateScaleDefaults('linear', {
      ticks: {
        min: 0,
      },
    });
  },
  mounted() {
    this.buildChart();
  },
  methods: {
    buildChart() {
      if (this.$store.getters.dataFormat === 'percentage') {
        this.chartOptions.scales.xAxes[0].ticks.max = this.maxPercentage;
      } else {
        this.chartOptions.scales.xAxes[0].ticks.max = this.maxDecimal;
      }

      try {
        const industryChartCTX = document.getElementById(this.canvasID).getContext('2d');

        if (window.industryChart[this.canvasID] !== undefined) {
          window.industryChart[this.canvasID].destroy();
        }
        if (this.displayMode === 'single' && window.industryChart.prevYear !== undefined) {
          window.industryChart.prevYear.destroy();
        }

        const clientDisplayNames = [];
        this.labels.forEach((clientName, key) => {
          if (clientName === 'industry_average') {
            clientDisplayNames.push('Industry Average');
          } else if (clientName.includes('Organization')) {
            let offset = 0;
            const rank = key + 1;
            if (key > this.industryIndex) { offset = 1; }
            clientDisplayNames.push(`Organisation ${rank - offset}`);
          } else {
            clientDisplayNames.push(this.$store.getters.getDisplayNameFromUsername(clientName));
          }
        });

        window.industryChart[this.canvasID] = new Chart(industryChartCTX, {
          type: 'horizontalBar',
          data: {
            labels: clientDisplayNames,
            datasets: [{
              data: this.chartData,
              backgroundColor: this.chartColors,
            }],
          },
          options: this.chartOptions,
        });
      } catch (err) {
        throw new Error(err.message);
      }
    },
    showSummaryPage(newClient) {
      this.$store.commit('updateCurrentClient', newClient.username);
      this.$store.commit('updateCurrentClientDisplayName', newClient.display_name);
      this.$store.commit('updateCurrentParamsHash');
      this.$root.$emit('updateClient', newClient);
      this.$router.push('/summary');
    },
    displayRankBadge(obj) {
      const { client } = obj;
      return (client === this.nameInLabels && this.singleClientMode);
    },
    formatClientName(string) {
      let returnValue;
      const currentClientDisplayName = this.$store.getters.getDisplayNameFromUsername(
        this.$store.getters.currentClient,
      );
      const { currentClient } = this.$store.getters;

      if (this.rareMode || string.includes('Industry')) {
        returnValue = this.$store.getters.getDisplayNameFromUsername(string);
      } else if (!this.rareMode && string === currentClient) {
        returnValue = `${currentClientDisplayName}`;
      } else {
        returnValue = string;
      }

      return (string === 'industry_average' ? 'Industry Average' : returnValue);
    },
    formatDataLabel(string) {
      let returnValue;
      if (string === 'N/A') {
        returnValue = string;
      } else {
        const percentage = (this.$store.getters.dataFormat === 'percentage' ? '%' : '');
        returnValue = `${string}${percentage}`;
      }
      return returnValue;
    },
  },
};
</script>

<style lang="scss" scoped>

  @import "~@/css/global.scss";

  .chart-container {
    margin-top: -13px;
  }

  .labelPadding {
    margin-left: 30px;
  }

  .label-list {
    list-style: none;
    margin-top: 8px;
  }

  .label-list li {
    margin-bottom: 18px;
    position: relative;
    min-height: 22px;
  }

  .label-list li span {
    color: #4a5b68;
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    letter-spacing: 1px;
    text-align: left;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .link {
    border-bottom: 1px dashed #4a5b68;
    cursor: pointer;
  }

  .data-list {
    list-style: none;
    margin: 8px 0 0 3px;
  }

  .data-list li {
    width: 50px;
    margin-bottom: 18px;
  }

  .data-list li span {
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    letter-spacing: 1px;
    text-align: right;
  }

  .legendDot {
    display: inline-block;
    height: 14px;
    width: 14px;
    font-size: 1px;
    border-radius: 7px;
    margin-left: 20px;
  }

  .legend-item {
    display: inline-block;
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    letter-spacing: 1px;
    text-align: left;
    margin: 8px 0 23px 7px;
  }

  .industry-overview-chart-container {
    // width: 700px;
  }

  .rank {
    float: right;
  }

  .rankBadge{
    border-bottom: none !important;
    color: #ffffff !important;
    height: 30px !important;
    width: 30px !important;
    border-radius: 15px !important;
    display:inline-block !important;
    text-align: center !important;
    line-height: 30px !important;
    position: absolute;
    top: -3px;
  }
</style>
